import {
    ChangeDetectionStrategy,
    Component,
    EmbeddedViewRef,
    Input,
    OnChanges,
    SimpleChanges,
    TemplateRef,
    ViewChild,
    ViewContainerRef
} from '@angular/core';

export type CompareFn = (cellA: CellNode, cellB: CellNode, rowA: RowNode, rowB: RowNode, def: Sort) => number;

export interface Column {
    id: string;
    width: number;
    compareFn?: CompareFn;

    type?: any;
}


////////////////////


interface Definition {
    tpl?: TemplateRef<any>;
    ctx?: any;
}

export interface Grid extends Definition {
}

export interface Row extends Definition {
    id: string;
    type?: any;
    height?: number;
    cells: Array<Cell>;
}

export interface Cell extends Definition {
    columnId: string;

    columnType?: any;
    sortClassList?: any;
}


////////////////////


export enum SortOrder {
    ASC,
    DESC
}

export interface Sort {
    columnId: string;
    order: SortOrder;
}

export function inverseSortOrder(column: Column, opts: Sort, defaultOrder: SortOrder): Sort {
    if (!column) return null;
    const same = opts && opts.columnId === column.id;
    return {
        columnId: column.id,
        order: same ? (opts.order === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC) : defaultOrder
    };
}


////////////////////


export interface Context {
    c?: Column;
    g?: GridNode;
    r?: RowNode;
    l?: CellNode;
    ctx?: any;
}

interface BaseNode<T = any> {
    context: Context;
    embeddedViewRef: EmbeddedViewRef<any>;
    element: HTMLElement;

    detectChanges(opts?: { onlySelf?: boolean; }): void;
}

function append<T extends BaseNode, U extends BaseNode>(renderer: Renderer, rendererNodeFn: (V) => T, collection: Array<T>, parentNode: U, defs: Array<any>): void {
    if (!renderer || !collection) return;
    defs.forEach(d => {
        const n = rendererNodeFn(d);
        collection.push(n);
        if (n instanceof RowNode) {
            n.cells.setChildren(d.cells);
        }
        if (parentNode instanceof GridNode) {
            renderer.appendChild(parentNode, n);
        }
        if (parentNode instanceof RowNode) {
            const nextEl = parentNode.children.length <= 1 /*current node is already pushed above*/ ? parentNode : parentNode.children[parentNode.children.length - 2];
            renderer.insertAfter(renderer.gridNode, n, nextEl);
        }
        if (parentNode instanceof NodeWithCells) {
            renderer.appendChild(parentNode, n);
        }
        n.detectChanges();
    });
}

function insert<T extends BaseNode, U extends BaseNode>(renderer: Renderer, rendererNodeFn: (V) => T, collection: Array<T>, parentNode: U, defs: Array<any>, index: number): void {
    if (!renderer || !collection) return;
    defs.forEach((d, i) => {
        const n = rendererNodeFn(d);
        collection.splice(index + i, 0, n);
        if (n instanceof RowNode) {
            n.cells.setChildren(d.cells);
        }
        if (parentNode instanceof GridNode || parentNode instanceof RowNode) {
            if (index === 0) {
                renderer.insertAfter(renderer.gridNode, n, parentNode);
            } else {
                renderer.insertAfter(renderer.gridNode, n, collection[index - 1]);
            }
        }
        if (parentNode instanceof NodeWithCells) {
            if (index === 0) {
                renderer.appendChild(parentNode, n);
            } else {
                renderer.insertAfter(parentNode, n, collection[index - 1]);
            }
        }
        n.detectChanges();
    });
}

function remove<T extends BaseNode, U extends BaseNode>(renderer: Renderer, collection: Array<T>, parentNode: U, start?: number, removeCount?: number) {
    if (!renderer || !collection) return;
    const _start = start || 0;
    const _removeCount = removeCount || collection.length - _start;
    collection.splice(_start, _removeCount).forEach(n => {
        if (n instanceof NodeWithChildren) {
            n.removeChildren();
        }
        if (n instanceof RowNode) {
            n.cells.removeChildren();
        }
        if (parentNode instanceof GridNode || parentNode instanceof RowNode) {
            renderer.removeChild(renderer.gridNode, n);
        }
        if (parentNode instanceof NodeWithCells) {
            renderer.removeChild(parentNode, n);
        }
        renderer.destroy(n);
    });
}

function replace<T extends BaseNode, U extends BaseNode>(renderer: Renderer, rendererNodeFn: (V) => T, collection: Array<T>, parentNode: U, def: any, index: number): void {
    if (index !== -1) {
        remove(renderer, collection, parentNode, index, 1);
        insert(renderer, rendererNodeFn, collection, parentNode, [def], index);
    } else {
        append(renderer, rendererNodeFn, collection, parentNode, [def]);
    }
}

function numberCompareFn(cellA: CellNode, cellB: CellNode, rowA: RowNode, rowB: RowNode, def: Sort): number {
    const retA = cellA.context.ctx == null ? Number.MIN_SAFE_INTEGER : cellA.context.ctx;
    const retB = cellB.context.ctx == null ? Number.MIN_SAFE_INTEGER : cellB.context.ctx;
    return def.order === SortOrder.ASC
        ? retA - retB
        : retB - retA;
}

function stringCompareFn(cellA: CellNode, cellB: CellNode, rowA: RowNode, rowB: RowNode, def: Sort): number {
    const retA = cellA.context.ctx == null ? '' : cellA.context.ctx;
    const retB = cellB.context.ctx == null ? '' : cellB.context.ctx;
    return def.order === SortOrder.ASC
        ? retA.localeCompare(retB)
        : retB.localeCompare(retA);
}

function rowCompareFn<T extends RowNode>(renderer: Renderer, a: T, b: T, def: Sort): number {
    const column = renderer.columns.find(c => c.id === def.columnId);
    const cellA = a.cells.find(l => l.def.columnId === def.columnId);
    const cellB = b.cells.find(l => l.def.columnId === def.columnId);
    return column.compareFn
        ? column.compareFn(cellA, cellB, a, b, def)
        : (typeof cellA.context.ctx === 'number'
            ? numberCompareFn(cellA, cellB, a, b, def)
            : stringCompareFn(cellA, cellB, a, b, def));
}

function cellCompareFn<T extends CellNode>(renderer: Renderer, a: T, b: T, def: Sort): number {
    const retA = renderer.columns.findIndex(c => c.id === a.def.columnId);
    const retB = renderer.columns.findIndex(c => c.id === b.def.columnId);
    return retA - retB;
}

type sortCompareFn<T> = (renderer: Renderer, a: T, b: T, def: Sort) => number;
type sortChildFn<T> = (child: T) => void;
type sortFilterFn<T> = (child: T) => boolean;

function sort<T extends BaseNode, U extends BaseNode>(
    renderer: Renderer,
    collection: Array<T>,
    parentNode: U,
    defs: Array<Sort>,
    compareFn: sortCompareFn<T>,
    childFn?: sortChildFn<T>,
    filterFn?: sortFilterFn<T>
): void {
    if (!renderer || !collection || !defs) return;
    const sorted = filterFn
        ? collection.filter(child => filterFn(child))
        : collection.slice();
    const unsorted = collection.map((child, index) => ({index: index, child: child}))
        .filter(indexChild => sorted.indexOf(indexChild.child) === -1);
    sorted.sort((a, b) => {
        let ret = 0;
        for (let i = 0; i < defs.length; i++) {
            const def = defs[i];
            ret = compareFn(renderer, a, b, def);
            if (ret !== 0) break;
        }
        return ret;
    });
    const sortedUnsorted = collection.slice()
        .sort((a, b) => sorted.indexOf(a) - sorted.indexOf(b));
    unsorted.forEach(indexChild => {
        const idx = sortedUnsorted.indexOf(indexChild.child);
        sortedUnsorted.splice(idx, 1);
        sortedUnsorted.splice(indexChild.index, 0, indexChild.child);
    });
    collection.sort((a, b) => sortedUnsorted.indexOf(a) - sortedUnsorted.indexOf(b));
    sorted.forEach(n => {
        if (parentNode instanceof GridNode || parentNode instanceof NodeWithCells) {
            renderer.appendChild(parentNode, n);
        }
        if (parentNode instanceof RowNode) {
            renderer.insertAfter(renderer.gridNode, n, parentNode);
        }
    });
    if (childFn) {
        collection.forEach(child => childFn(child));
    }
}


////////////////////


export class CellNode implements BaseNode {
    def: Cell;
    context: Context;
    embeddedViewRef: EmbeddedViewRef<any>;
    element: HTMLElement;

    constructor(protected _renderer: Renderer) {
    }

    detectChanges(opts?: { onlySelf?: boolean; }): void {
        try {
            this.embeddedViewRef.detectChanges();
        } catch {
        }
    }
}

abstract class NodeArray<T> {
    abstract nodes(): Array<T>;

    indexOf(node: T): number {
        return this.nodes().indexOf(node);
    }

    length(): number {
        return this.nodes().length;
    }

    at(index: number): T {
        return this.nodes()[index];
    }

    find(fn: (node: T) => boolean): T {
        for (const n in this.nodes()) {
            const node = this.nodes()[n];
            if (node instanceof NodeArray) {
                const found = node.find(fn);
                if (found) return found;
            } else {
                if (fn(node)) return node;
            }
        }
        return undefined;
    }

    filter(fn: (node: T) => boolean): Array<T> {
        let filtered = [];
        this.nodes().forEach(node => {
            if (node instanceof NodeArray) {
                filtered = filtered.concat(node.filter(fn));
            } else {
                if (fn(node)) {
                    filtered = filtered.concat(node);
                }
            }
        });
        return filtered;
    }

    forEach(fn: (node: T) => void): void {
        this.nodes().forEach(node => {
            if (node instanceof NodeArray) {
                node.forEach(fn);
            } else {
                fn(node);
            }
        });
    }
}

abstract class NodeWithChildren<T extends BaseNode, U extends BaseNode, V> extends NodeArray<T> implements BaseNode {
    abstract context: Context;
    abstract embeddedViewRef: EmbeddedViewRef<any>;
    abstract element: HTMLElement;

    parent: U;
    children: Array<T>;

    abstract renderNodeFn(): (def: V) => T;

    sortCompareFn?(defs: Array<Sort>, filterFn?: sortFilterFn<T>): sortCompareFn<T>;

    sortChildFn?(defs: Array<Sort>, filterFn?: sortFilterFn<T>): sortChildFn<T>;

    protected constructor(protected _renderer: Renderer, protected _parentIsTypeofChild?: boolean) {
        super();
        this.children = [];
    }

    detectChanges(opts?: { onlySelf?: boolean; }): void {
        if (opts && opts.onlySelf) return;
        this.children.forEach(n => n.detectChanges());
    }

    nodes(): Array<T> {
        return this.children;
    }

    find(fn: (node: T) => boolean): T {
        if (this._parentIsTypeofChild) {
            if (fn(this as any as T)) return this as any as T;
        }
        return super.find(fn);
    }

    filter(fn: (node: T) => boolean): Array<T> {
        let filtered = [];
        if (this._parentIsTypeofChild) {
            if (fn(this as any as T)) filtered.push(this);
        }
        filtered = filtered.concat(super.filter(fn));
        return filtered;
    }

    forEach(fn: (node: T) => void): void {
        if (this._parentIsTypeofChild) {
            fn(this as any as T);
        }
        super.forEach(fn);
    }

    setChildren(defs: Array<V>): void {
        this.removeChildren();
        this.appendChildren(defs);
    }

    appendChildren(defs: Array<V>): void {
        append(this._renderer, this.renderNodeFn(), this.children, this as any as U, defs);
    }

    insertChildren(defs: Array<V>, index: number): void {
        insert(this._renderer, this.renderNodeFn(), this.children, this as any as U, defs, index);
    }

    removeChildren(start?: number, removeCount?: number): void {
        remove(this._renderer, this.children, this as any as U, start, removeCount);
    }

    setChild(def: V, index: number): void {
        replace(this._renderer, this.renderNodeFn(), this.children, this as any as U, def, index);
    }

    sort(defs: Array<Sort>, filterFn?: sortFilterFn<T>): void {
        sort(this._renderer, this.children, this as any as U, defs, this.sortCompareFn(defs, filterFn), !this.sortChildFn ? null : this.sortChildFn(defs, filterFn), filterFn);
    }
}

class NodeWithCells extends NodeWithChildren<CellNode, RowNode, Cell> {
    get def(): Row {
        return this.parent.def;
    }

    get context(): Context {
        return this.parent.context;
    }

    get embeddedViewRef(): EmbeddedViewRef<any> {
        return this.parent.embeddedViewRef;
    }

    get element(): HTMLElement {
        return this.parent.element;
    }

    renderNodeFn(): (def: Cell) => CellNode {
        return def => this._renderer.cell(def, this.parent);
    }

    sortCompareFn(defs: Array<Sort>, filterFn?: sortFilterFn<CellNode>): sortCompareFn<CellNode> {
        return (renderer: Renderer, a: CellNode, b: CellNode, def: Sort) => cellCompareFn(this._renderer, a, b, def);
    }

    setChildByColumnId(def: Cell, columnId: string): void {
        const index = this.indexOf(this.find(n => n.context.c.id === columnId));
        super.setChild(def, index);
    }
}

export class RowNode extends NodeWithChildren<RowNode, GridNode | RowNode, Row> implements BaseNode {
    def: Row;
    context: Context;
    embeddedViewRef: EmbeddedViewRef<any>;
    element: HTMLElement;
    cells: NodeWithCells;

    constructor(protected _renderer: Renderer) {
        super(_renderer, true);
        this.cells = new NodeWithCells(this._renderer);
        this.cells.parent = this;
    }

    detectChanges(opts?: { onlySelf?: boolean; }): void {
        if (!opts || !opts.onlySelf) {
            super.detectChanges();
        }
        this.cells.detectChanges();
        try {
            this.embeddedViewRef.detectChanges();
        } catch {
        }
    }

    renderNodeFn(): (def: Row) => RowNode {
        return def => this._renderer.row(def, this);
    }

    sortCompareFn(defs: Array<Sort>, filterFn?: sortFilterFn<RowNode>): sortCompareFn<RowNode> {
        return (renderer: Renderer, a: RowNode, b: RowNode, def: Sort) => rowCompareFn(this._renderer, a, b, def);
    }

    sortChildFn(defs: Array<Sort>, filterFn?: sortFilterFn<RowNode>): sortChildFn<RowNode> {
        return row => row.sort(defs, filterFn);
    }
}

export class GridNode extends NodeWithChildren<RowNode, GridNode, Row> implements BaseNode {
    def: Grid;
    context: Context;
    embeddedViewRef: EmbeddedViewRef<any>;
    element: HTMLElement;

    constructor(protected _renderer: Renderer) {
        super(_renderer);
    }

    detectChanges(opts?: { onlySelf?: boolean; }): void {
        if (!opts || !opts.onlySelf) {
            super.detectChanges();
        }
        try {
            this.embeddedViewRef.detectChanges();
        } catch {
        }
    }

    renderNodeFn(): (def: Row) => RowNode {
        return def => this._renderer.row(def, this);
    }

    sortCompareFn(defs: Array<Sort>, filterFn?: sortFilterFn<RowNode>): sortCompareFn<RowNode> {
        return (renderer: Renderer, a: RowNode, b: RowNode, def: Sort) => rowCompareFn(this._renderer, a, b, def);
    }

    sortChildFn(defs: Array<Sort>, filterFn?: sortFilterFn<RowNode>): sortChildFn<RowNode> {
        return row => row.sort(defs, filterFn);
    }
}

function configureNode(node: BaseNode, context: Context, tpl: TemplateRef<any>): void {
    node.context = context;
    node.embeddedViewRef = tpl.createEmbeddedView(node.context);
    if (node.embeddedViewRef.rootNodes.length !== 1) {
        throw new Error('single root node required');
    }
    node.element = node.embeddedViewRef.rootNodes[0];
}

function destroyNode(node: BaseNode): void {
    node.element = null;
    node.embeddedViewRef.destroy();
}

function getElement(parentNode: BaseNode, node: BaseNode): HTMLElement {
    return parentNode.element;
}

class Renderer {
    defaultGridTpl: TemplateRef<any>;
    defaultRowTpl: TemplateRef<any>;
    defaultCellTpl: TemplateRef<any>;
    columns: Array<Column>;
    gridNode: GridNode;

    constructor(public readonly opts?: any) {
    }

    grid(grid: Grid): GridNode {
        const node = new GridNode(this);
        node.def = grid;
        configureNode(node,
            {g: node, ctx: grid.ctx},
            (grid.tpl || this.defaultGridTpl));
        return node;
    }

    row(row: Row, parentNode: GridNode | RowNode): RowNode {
        const node = new RowNode(this);
        node.def = row;
        node.parent = parentNode;
        configureNode(node,
            {r: node, ctx: row.ctx},
            (row.tpl || this.defaultRowTpl));
        return node;
    }

    cell(cell: Cell, parentNode: RowNode): CellNode {
        const node = new CellNode(this);
        node.def = cell;
        configureNode(node,
            {c: this.columns.find(c => c.id === cell.columnId), r: parentNode, l: node, ctx: cell.ctx},
            (cell.tpl || this.defaultCellTpl));
        return node;
    }

    destroy(node: BaseNode): void {
        destroyNode(node);
    }

    appendChild(parentNode: BaseNode, node: BaseNode): void {
        getElement(parentNode, node).appendChild(node.element);
    }

    insertBefore(parentNode: BaseNode, node: BaseNode, siblingNode: BaseNode): void {
        getElement(parentNode, node).insertBefore(node.element, siblingNode.element);
    }

    insertAfter(parentNode: BaseNode, node: BaseNode, siblingNode: BaseNode): void {
        getElement(parentNode, node).insertBefore(node.element, siblingNode.element.nextSibling);
    }

    removeChild(parentNode: BaseNode, node: BaseNode): void {
        if (!node.element.parentElement) return;
        getElement(parentNode, node).removeChild(node.element);
    }
}

abstract class Api {
    protected columns: Array<Column>;
    protected _renderer: Renderer;
    protected _grid: GridNode;

    get grid(): GridNode {
        return this._grid;
    }
}

@Component({
    selector: '[grid]',
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <ng-template #gridTpl>
            <div></div>
        </ng-template>

        <ng-template #rowTpl>
            <div></div>
        </ng-template>

        <ng-template #cellTpl let-ctx="ctx">
            <div>{{ctx}}</div>
        </ng-template>
    `,
    exportAs: 'grid'
})
export class GridComponent extends Api implements OnChanges {
    @ViewChild('gridTpl', { static: true }) private _gridTpl: TemplateRef<any>;
    @ViewChild('rowTpl', { static: true }) private _rowTpl: TemplateRef<any>;
    @ViewChild('cellTpl', { static: true }) private _cellTpl: TemplateRef<any>;
    @Input() gridTpl: TemplateRef<any>;
    @Input() rowTpl: TemplateRef<any>;
    @Input() cellTpl: TemplateRef<any>;
    @Input() columns: Array<Column>;

    constructor(private _viewContainerRef: ViewContainerRef) {
        super();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.hasOwnProperty('columns')) {
            this._render();
        }
    }

    private _render(): void {
        if (this._grid) {
            this._grid.removeChildren();
            this._viewContainerRef.element.nativeElement.removeChild(this._grid.element);
            this._renderer.destroy(this._grid);
        }

        if (!this.columns) return;

        this._renderer = new Renderer();
        this._renderer.defaultGridTpl = this.gridTpl || this._gridTpl;
        this._renderer.defaultRowTpl = this.rowTpl || this._rowTpl;
        this._renderer.defaultCellTpl = this.cellTpl || this._cellTpl;
        this._renderer.columns = this.columns;
        this._grid = this._renderer.grid({
            tpl: this.gridTpl
        });
        this._viewContainerRef.element.nativeElement.appendChild(this._grid.element);
        this._renderer.gridNode = this._grid;
    }
}
